// Layout Context for managing responsive layout state

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { LAYOUT_TYPES, detectLayout, getLayoutConfig, isLayoutSuitable } from '../constants/layoutConfigs';
import {
  SECTIONS,
  detectDeviceType,
  getSectionProportions,
  isStatsVisible,
  getFlexValues
} from '../constants/proportionalLayout';
import { estimatePhysicalSize, computeAdaptiveLayout } from '../constants/adaptiveLayout';
import { logger } from '../utils/logger';

const log = logger.child('LayoutContext');

const LayoutContext = createContext();

const STORAGE_KEY = 'steadily_layout_preference';
const AUTO_PREFERENCE_KEY = 'steadily_layout_auto';

// Set --app-height CSS variable to the full viewport height. We now handle
// status-bar / nav-bar overlap via env(safe-area-inset-*) padding on #root,
// so the old "subtract 48 when innerHeight matches screen short side" hack
// double-subtracted on Android 15+ (edge-to-edge + safe-area padding) and
// left the app painting into a ~315 CSS-px content area on a 411-px viewport.
const setAppHeight = () => {
  document.documentElement.style.setProperty('--app-height', window.innerHeight + 'px');
};
setAppHeight();

// Effective content dimensions — what #root can actually use after safe-area
// insets are applied. When system bars toggle, env(safe-area-inset-*) changes
// and this returns a different value even though innerWidth/innerHeight don't.
const getEffectiveSize = () => {
  const root = document.getElementById('root');
  if (root) {
    // clientWidth/clientHeight exclude padding — exactly what we want: the
    // area the app actually has to render into.
    return { width: root.clientWidth, height: root.clientHeight };
  }
  return { width: window.innerWidth, height: window.innerHeight };
};

export const LayoutProvider = ({ children }) => {
  // Initialize with auto-detection. Use effective size (accounting for
  // safe-area-inset padding on #root) rather than raw innerWidth/innerHeight,
  // so the adaptive layout knows whether the system bars are taking up space.
  const [screenSize, setScreenSize] = useState(() => getEffectiveSize());
  
  const [autoDetectedLayout, setAutoDetectedLayout] = useState(() => 
    detectLayout(window.innerWidth, window.innerHeight)
  );
  
  const [isAutoMode, setIsAutoMode] = useState(() => {
    try {
      const saved = localStorage.getItem(AUTO_PREFERENCE_KEY);
      return saved === null ? true : saved === 'true';
    } catch (e) {
      // Restricted-storage environments (Safari private mode, some embedded
      // WebViews) throw synchronously here. Default to auto.
      log.warn('localStorage read failed', e, { key: AUTO_PREFERENCE_KEY });
      return true;
    }
  });
  
  const [currentLayout, setCurrentLayout] = useState(() => {
    try {
      if (isAutoMode) {
        return detectLayout(window.innerWidth, window.innerHeight);
      }
      const savedLayout = localStorage.getItem(STORAGE_KEY);
      if (savedLayout && Object.values(LAYOUT_TYPES).includes(savedLayout)) {
        if (isLayoutSuitable(savedLayout, window.innerWidth)) {
          return savedLayout;
        }
      }
      return detectLayout(window.innerWidth, window.innerHeight);
    } catch (e) {
      log.warn('layout init failed', e, { key: STORAGE_KEY });
      return LAYOUT_TYPES.DESKTOP_LANDSCAPE;
    }
  });

  const [config, setConfig] = useState(() => {
    var resolved = getLayoutConfig(currentLayout);
    return resolved || getLayoutConfig(LAYOUT_TYPES.DESKTOP_LANDSCAPE);
  });

  // Proportional layout system state
  const [deviceType, setDeviceType] = useState(() =>
    detectDeviceType(window.innerWidth)
  );

  // Habit count for adaptive layout (updated by AppContent via updateHabitCount)
  const [habitCount, setHabitCount] = useState(0);

  // Adaptive layout computation - recomputes when screen size or habit count changes
  const adaptiveResult = useMemo(() => {
    const physical = estimatePhysicalSize();
    const orientation = screenSize.width > screenSize.height ? 'landscape' : 'portrait';
    return computeAdaptiveLayout(physical.diagonalInches, orientation, habitCount);
  }, [screenSize.width, screenSize.height, habitCount]);

  // Memoized proportional layout values (legacy, used when manual override is active)
  const legacyProportionalLayout = useMemo(() => {
    const proportions = getSectionProportions(deviceType);
    const flexValues = getFlexValues(deviceType);
    const showStats = isStatsVisible(deviceType);

    return {
      deviceType,
      proportions,
      flexValues,
      showStats,
      // CSS-ready flex values for styling
      yearDotsFlex: flexValues[SECTIONS.YEAR_DOTS],
      weekViewFlex: flexValues[SECTIONS.WEEK_VIEW],
      statsFlex: flexValues[SECTIONS.STATS],
      // Percentage strings for inline styles if needed
      yearDotsPercent: `${proportions[SECTIONS.YEAR_DOTS] * 100}%`,
      weekViewPercent: `${proportions[SECTIONS.WEEK_VIEW] * 100}%`,
      statsPercent: `${proportions[SECTIONS.STATS] * 100}%`
    };
  }, [deviceType]);

  // Effective layout: adaptive when auto mode, legacy when manual override
  const proportionalLayout = isAutoMode ? adaptiveResult.proportionalLayout : legacyProportionalLayout;

  // Effective config: adaptive when auto mode, static when manual override
  const effectiveConfig = isAutoMode ? adaptiveResult.config : config;

  // Callback for AppContent to sync habit count into the layout system
  const updateHabitCount = useCallback((count) => {
    setHabitCount(count);
  }, []);

  // Handle screen resize with debouncing
  useEffect(() => {
    let timeoutId;
    
    const handleResize = () => {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        const eff = getEffectiveSize();

        setAppHeight();
        setScreenSize(eff);

        // Update proportional layout device type
        const newDeviceType = detectDeviceType(eff.width);
        setDeviceType(newDeviceType);

        const detectedLayout = detectLayout(eff.width, eff.height);
        setAutoDetectedLayout(detectedLayout);

        if (isAutoMode) {
          setCurrentLayout(detectedLayout);
          setConfig(getLayoutConfig(detectedLayout));
        } else {
          // Check if current manual selection is still suitable
          if (!isLayoutSuitable(currentLayout, eff.width)) {
            // Force switch to auto mode if layout becomes unsuitable
            setIsAutoMode(true);
            setCurrentLayout(detectedLayout);
            setConfig(getLayoutConfig(detectedLayout));
            try { localStorage.setItem(AUTO_PREFERENCE_KEY, 'true'); } catch {}
          }
        }
      }, 150); // Debounce resize events
    };

    window.addEventListener('resize', handleResize);

    // Watch #root's own content box. When system bars toggle, `innerHeight`
    // stays constant but the safe-area-inset padding on #root changes, so
    // root.clientHeight flips between e.g. 914 and 866. ResizeObserver fires
    // when the padding changes and feeds the new effective size into the
    // layout calculations — same path as orientation change.
    const root = document.getElementById('root');
    let resizeObserver = null;
    if (root && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(root);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [isAutoMode, currentLayout]);

  // Switch to a specific layout
  const switchLayout = useCallback((layoutType) => {
    if (!Object.values(LAYOUT_TYPES).includes(layoutType)) {
      log.warn('invalid layout type', { layoutType });
      return;
    }

    if (!isLayoutSuitable(layoutType, screenSize.width)) {
      log.warn('layout not suitable for screen size', { layoutType, width: screenSize.width });
      return;
    }
    
    setCurrentLayout(layoutType);
    setConfig(getLayoutConfig(layoutType));
    setIsAutoMode(false);

    // Persist preference
    try {
      localStorage.setItem(STORAGE_KEY, layoutType);
      localStorage.setItem(AUTO_PREFERENCE_KEY, 'false');
    } catch {}
  }, [screenSize.width]);

  // Enable auto mode
  const enableAutoMode = useCallback(() => {
    setIsAutoMode(true);
    const detected = detectLayout(screenSize.width, screenSize.height);
    setAutoDetectedLayout(detected);
    setCurrentLayout(detected);
    setConfig(getLayoutConfig(detected));
    
    // Persist preference
    try {
      localStorage.setItem(AUTO_PREFERENCE_KEY, 'true');
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, [screenSize]);

  // Get available layouts for current screen size
  const getAvailableLayouts = useCallback(() => {
    return Object.values(LAYOUT_TYPES).filter(type => 
      isLayoutSuitable(type, screenSize.width)
    );
  }, [screenSize.width]);

  // Cheap stable `isLayoutSuitable` wrapper so consumers don't see a new
  // function identity every render (would break consumer memoization).
  const isLayoutSuitableForCurrentWidth = useCallback(
    (type) => isLayoutSuitable(type, screenSize.width),
    [screenSize.width]
  );

  const value = useMemo(() => ({
    // Current state
    currentLayout,
    config: effectiveConfig,
    isAutoMode,
    autoDetectedLayout,
    screenSize,

    // Proportional layout system
    proportionalLayout,
    deviceType,

    // Adaptive layout
    habitCount,
    updateHabitCount,

    // Actions
    switchLayout,
    enableAutoMode,
    getAvailableLayouts,

    // Utility
    isLayoutSuitable: isLayoutSuitableForCurrentWidth
  }), [
    currentLayout,
    effectiveConfig,
    isAutoMode,
    autoDetectedLayout,
    screenSize,
    proportionalLayout,
    deviceType,
    habitCount,
    updateHabitCount,
    switchLayout,
    enableAutoMode,
    getAvailableLayouts,
    isLayoutSuitableForCurrentWidth,
  ]);

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

// Custom hook to use layout context
export const useLayout = () => {
  const context = useContext(LayoutContext);
  
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  
  return context;
};

export default LayoutContext;

